import React, { useState, useEffect, useCallback } from 'react';
import { Search, Users, RefreshCw, Sparkles, Eye, Building2, Filter, Star, Briefcase } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';
import CandidateEvidencePanel from '../components/talent/CandidateEvidencePanel';

const WORKFLOW_STAGES = [
  { value: 'All',          label: 'All Stages'      },
  { value: 'JD',           label: 'Job Description' },
  { value: 'Media',        label: 'Media & Content' },
  { value: 'Assessment',   label: 'Assessment'      },
  { value: 'Resumes',      label: 'Resumes'         },
  { value: 'Offer Letter', label: 'Offer Letter'    },
];

// ── Candidate Potential config ────────────────────────────────────────
const POTENTIAL_OPTIONS = [
  { value: 'high_potential',   label: 'High Potential',   cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { value: 'future_fit',       label: 'Future Fit',       cls: 'bg-indigo-100  text-indigo-700  border-indigo-300'  },
  { value: 'silver_medalist',  label: 'Silver Medalist',  cls: 'bg-slate-100   text-slate-600   border-slate-300'   },
  { value: 'reconsider_later', label: 'Reconsider Later', cls: 'bg-amber-100   text-amber-700   border-amber-300'   },
  { value: 'not_suitable',     label: 'Not Suitable',     cls: 'bg-rose-100    text-rose-600    border-rose-300'    },
];
const POTENTIAL_MAP = Object.fromEntries(POTENTIAL_OPTIONS.map(o => [o.value, o]));

function PotentialDropdown({ resumeId, value, onChange }) {
  const [saving, setSaving] = useState(false);
  const handleChange = async (e) => {
    const next = e.target.value || null;
    setSaving(true);
    try {
      await api.patch(`/resumes/${resumeId}/potential`, { potential: next });
      onChange(resumeId, next);
    } catch {
      toast.error('Failed to save potential');
    } finally {
      setSaving(false);
    }
  };
  const current = value ? POTENTIAL_MAP[value] : null;
  return (
    <div className={`relative inline-flex items-center rounded-lg border text-xs font-semibold overflow-hidden
      ${current ? current.cls : 'bg-gray-50 text-gray-400 border-gray-200'}`}>
      {saving && (
        <span className="absolute inset-0 flex items-center justify-center bg-white/70 z-10">
          <RefreshCw className="w-3 h-3 animate-spin text-gray-500" />
        </span>
      )}
      <select
        value={value || ''}
        onChange={handleChange}
        disabled={saving}
        className="appearance-none bg-transparent pl-2.5 pr-6 py-1.5 focus:outline-none cursor-pointer"
        style={{ minWidth: 138 }}
      >
        <option value="">Set potential…</option>
        {POTENTIAL_OPTIONS.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <span className="pointer-events-none absolute right-2 opacity-60">▾</span>
    </div>
  );
}

const STAGE_STYLE = (stage = '') => {
  if (stage === 'Offer Letter') return 'bg-emerald-100 text-emerald-700';
  if (stage === 'Assessment')   return 'bg-violet-100 text-violet-700';
  if (stage === 'Resumes')      return 'bg-orange-100 text-orange-700';
  if (stage === 'Media')        return 'bg-pink-100 text-pink-700';
  if (stage === 'JD')           return 'bg-indigo-100 text-indigo-700';
  return 'bg-gray-100 text-gray-500';
};

const MATCH_COLOR = score =>
  score >= 80 ? 'text-emerald-600' :
  score >= 60 ? 'text-orange-500' : 'text-red-400';

// Reusable filter pill — label on top, icon + select below
function FilterPill({ icon, label, children, active = false }) {
  return (
    <div className={`flex flex-col gap-0.5 px-3 py-2 rounded-xl border transition-all
      ${active
        ? 'bg-orange-50 border-orange-200'
        : 'bg-gray-50 border-gray-200 hover:border-gray-300'}`}>
      <span className={`text-[10px] font-semibold uppercase tracking-wide flex items-center gap-1
        ${active ? 'text-orange-500' : 'text-gray-400'}`}>
        {icon}{label}
      </span>
      <div className="flex items-center">
        {children}
      </div>
    </div>
  );
}

export default function TalentPool() {
  const [candidates, setCandidates]   = useState([]);
  const [total, setTotal]             = useState(0);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]               = useState('');
  const [stageFilter, setStageFilter]     = useState('All');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [companyId, setCompanyId]         = useState('all');
  const [minScore, setMinScore]           = useState('');
  const [potentialFilter, setPotentialFilter] = useState('all');
  const [jdFilter, setJdFilter]           = useState('all');
  const [jdList, setJdList]               = useState([]);
  const [companies, setCompanies]         = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [generatingFor, setGeneratingFor]         = useState(null);

  // Fetch all companies on mount
  useEffect(() => {
    api.get('/companies')
      .then(r => {
        const list = r.data?.data || r.data?.companies || r.data || [];
        setCompanies(Array.isArray(list) ? list.map(c => ({ _id: c._id, name: c.name || c.companyName })).filter(c => c.name) : []);
      })
      .catch(() => {});
  }, []);

  // Fetch JDs — re-runs whenever companyId changes
  const loadJDs = useCallback((cid = 'all') => {
    const params = cid && cid !== 'all' ? `?companyId=${cid}` : '';
    api.get(`/talent-pool/jobs${params}`)
      .then(r => setJdList(r.data?.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => { loadJDs(); }, []); // eslint-disable-line

  // Accept overrides so callers can pass fresh values before state updates settle
  const load = useCallback(async (overrides = {}) => {
    try {
      setLoading(true);
      const s   = overrides.search          ?? search;
      const st  = overrides.stageFilter     ?? stageFilter;
      const ms  = overrides.minScore        ?? minScore;
      const cid = overrides.companyId       ?? companyId;
      const pf  = overrides.potentialFilter ?? potentialFilter;
      const jdf = overrides.jdFilter        ?? jdFilter;

      const params = new URLSearchParams();
      if (s)                      params.append('search',        s);
      if (st && st !== 'All')     params.append('workflowStage', st);
      if (ms)                     params.append('minMatchScore', ms);
      if (cid && cid !== 'all')   params.append('companyId',    cid);
      params.append('limit', '100');

      const r = await api.get(`/talent-pool?${params.toString()}`);
      let fetched = r.data?.data?.candidates || [];
      // Client-side potential filter
      if (pf && pf !== 'all') {
        fetched = fetched.filter(c => c.candidatePotential === pf);
      }
      // Client-side JD filter
      if (jdf && jdf !== 'all') {
        fetched = fetched.filter(c => c.appliedJDId?.toString() === jdf);
      }
      setCandidates(fetched);
      setTotal(fetched.length);
    } catch (e) {
      toast.error('Failed to load talent pool');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initial load
  useEffect(() => { load(); }, []); // eslint-disable-line

  // Update potential locally after save (no full reload needed)
  const handlePotentialChange = (resumeId, newValue) => {
    setCandidates(prev => prev.map(c =>
      c._id === resumeId ? { ...c, candidatePotential: newValue } : c
    ));
  };

  const handleGenerateRecs = async (candidate) => {
    try {
      setGeneratingFor(candidate._id);
      const r = await api.post(`/talent-pool/${candidate._id}/recommendations`);
      const recs = r.data?.data || [];
      // Update locally
      setCandidates(prev => prev.map(c =>
        c._id === candidate._id ? { ...c, recommendations: recs, recommendationsGeneratedAt: new Date() } : c
      ));
      // Update selected panel if open
      if (selectedCandidate?._id === candidate._id) {
        setSelectedCandidate(prev => ({ ...prev, recommendations: recs }));
      }
      toast.success('AI recommendations generated');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to generate recommendations');
    } finally {
      setGeneratingFor(null);
    }
  };

  return (
    <div className="p-6 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-black text-gray-900">
            Talent <span className="text-orange-500">Pool</span>
          </h1>
          <p className="text-xs text-gray-400 mt-0.5">
            {total} candidate{total !== 1 ? 's' : ''} across all JDs
          </p>
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-1.5 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-semibold hover:bg-orange-600 disabled:opacity-60 transition-colors">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Reload
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-5 space-y-3">
        {/* Row 1: Search + Company + Stage */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Search */}
          <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 flex-1 min-w-[220px] focus-within:border-orange-300 focus-within:ring-1 focus-within:ring-orange-100 transition-all">
            <Search className="w-4 h-4 text-gray-400 shrink-0" />
            <input
              type="text" placeholder="Search by name or email…"
              value={search}
              onChange={e => { setSearch(e.target.value); load({ search: e.target.value, stageFilter, minScore, companyId }); }}
              className="bg-transparent text-sm focus:outline-none w-full text-gray-700 placeholder-gray-400"
            />
          </div>

          {/* Company filter */}
          <FilterPill icon={<Building2 className="w-3.5 h-3.5" />} label="Company">
            <select value={companyFilter}
              onChange={e => {
                const name = e.target.value;
                const found = companies.find(c => c.name === name);
                const id = name === 'all' ? 'all' : (found?._id || 'all');
                setCompanyFilter(name);
                setCompanyId(id);
                setJdFilter('all');
                loadJDs(id);
                load({ companyId: id, search, stageFilter, minScore, jdFilter: 'all' });
              }}
              className="bg-transparent text-sm font-medium text-gray-700 focus:outline-none cursor-pointer">
              <option value="all">All Companies</option>
              {companies.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
            </select>
          </FilterPill>

          {/* JD filter */}
          <FilterPill icon={<Briefcase className="w-3.5 h-3.5" />} label="JD" active={jdFilter !== 'all'}>
            <select value={jdFilter}
              onChange={e => {
                setJdFilter(e.target.value);
                load({ jdFilter: e.target.value, search, stageFilter, minScore, companyId, potentialFilter });
              }}
              className="bg-transparent text-sm font-medium text-gray-700 focus:outline-none cursor-pointer"
              style={{ maxWidth: 200 }}>
              <option value="all">All JDs</option>
              {jdList.map(j => (
                <option key={j._id} value={j._id}>
                  {j.title}{j.companyName ? ` — ${j.companyName}` : ''}
                </option>
              ))}
            </select>
          </FilterPill>

          {/* Workflow stage */}
          <FilterPill icon={<Filter className="w-3.5 h-3.5" />} label="Stage">
            <select value={stageFilter}
              onChange={e => { setStageFilter(e.target.value); load({ stageFilter: e.target.value, search, minScore, companyId }); }}
              className="bg-transparent text-sm font-medium text-gray-700 focus:outline-none cursor-pointer">
              {WORKFLOW_STAGES.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </FilterPill>

          {/* Min match score */}
          <FilterPill icon={<span className="text-[10px] font-black text-orange-400">%</span>} label="Min Match">
            <select value={minScore}
              onChange={e => { setMinScore(e.target.value); load({ minScore: e.target.value, search, stageFilter, companyId, potentialFilter }); }}
              className="bg-transparent text-sm font-medium text-gray-700 focus:outline-none cursor-pointer">
              <option value="">Any</option>
              <option value="40">40%+</option>
              <option value="60">60%+</option>
              <option value="70">70%+</option>
              <option value="80">80%+</option>
            </select>
          </FilterPill>

          {/* Candidate Potential filter */}
          <FilterPill icon={<Star className="w-3.5 h-3.5" />} label="Potential" active={potentialFilter !== 'all'}>
            <select value={potentialFilter}
              onChange={e => {
                setPotentialFilter(e.target.value);
                load({ potentialFilter: e.target.value, search, stageFilter, minScore, companyId });
              }}
              className="bg-transparent text-sm font-medium text-gray-700 focus:outline-none cursor-pointer">
              <option value="all">All Potential</option>
              {POTENTIAL_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </FilterPill>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
          </div>
        ) : candidates.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="font-semibold text-gray-500">No candidates found</p>
            <p className="text-sm mt-1">Try adjusting your filters</p>
          </div>
        ) : (() => {
            const displayed = candidates;
            return (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left py-3 px-4 text-xs font-bold text-gray-400 uppercase tracking-wide">Candidate</th>
                  <th className="text-left py-3 px-4 text-xs font-bold text-gray-400 uppercase tracking-wide">Applied JD</th>
                  <th className="text-center py-3 px-4 text-xs font-bold text-gray-400 uppercase tracking-wide">Stage</th>
                  <th className="text-center py-3 px-4 text-xs font-bold text-gray-400 uppercase tracking-wide">Match</th>
                  <th className="text-left py-3 px-4 text-xs font-bold text-gray-400 uppercase tracking-wide">Recommendations</th>
                  <th className="text-left py-3 px-4 text-xs font-bold text-gray-400 uppercase tracking-wide">
                    <span className="flex items-center gap-1"><Star className="w-3 h-3 text-orange-400" />Potential</span>
                  </th>
                  <th className="text-center py-3 px-4 text-xs font-bold text-gray-400 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayed.map((c, i) => (
                  <tr key={c._id} className={`border-b border-gray-50 hover:bg-orange-50 transition-colors ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>

                    {/* Candidate */}
                    <td className="py-3.5 px-4">
                      <p className="font-semibold text-gray-800">{c.candidateName}</p>
                      <p className="text-xs text-gray-400 truncate max-w-[160px]">{c.email}</p>
                      {c.companyName && (
                        <p className="text-xs text-orange-400 flex items-center gap-1 mt-0.5">
                          <Building2 className="w-3 h-3" />{c.companyName}
                        </p>
                      )}
                    </td>

                    {/* Applied JD */}
                    <td className="py-3.5 px-4">
                      <p className="text-gray-700 font-medium max-w-[160px] truncate">{c.appliedJD}</p>
                    </td>

                    {/* Stage */}
                    <td className="py-3.5 px-4 text-center">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${STAGE_STYLE(c.workflowStage)}`}>
                        {c.workflowStage}
                      </span>
                    </td>

                    {/* Match score */}
                    <td className="py-3.5 px-4 text-center">
                      <span className={`text-xl font-extrabold ${MATCH_COLOR(c.matchScore)}`}>
                        {c.matchScore}%
                      </span>
                    </td>

                    {/* Recommendations */}
                    <td className="py-3.5 px-4">
                      {c.recommendations.length > 0 ? (
                        <div className="space-y-1">
                          {c.recommendations.slice(0, 2).map((rec, ri) => (
                            <div key={ri} className="flex items-center gap-1.5">
                              <span className="text-xs font-bold text-violet-600">{rec.matchScore}%</span>
                              <span className="text-xs text-gray-600 truncate max-w-[120px]">{rec.jobTitle}</span>
                            </div>
                          ))}
                          {c.recommendations.length > 2 && (
                            <button
                              onClick={() => setSelectedCandidate(c)}
                              className="text-[10px] text-violet-500 hover:underline font-medium"
                            >
                              +{c.recommendations.length - 2} more
                            </button>
                          )}
                        </div>
                      ) : (
                        <button
                          onClick={() => handleGenerateRecs(c)}
                          disabled={generatingFor === c._id}
                          className="flex items-center gap-1 px-2.5 py-1.5 border border-violet-200 text-violet-600 rounded-lg text-xs font-medium hover:bg-violet-50 disabled:opacity-50 transition-colors"
                        >
                          {generatingFor === c._id
                            ? <><RefreshCw className="w-3 h-3 animate-spin" /> Generating…</>
                            : <><Sparkles className="w-3 h-3" /> Generate</>}
                        </button>
                      )}
                    </td>

                    {/* Candidate Potential */}
                    <td className="py-3.5 px-4">
                      <PotentialDropdown
                        resumeId={c._id}
                        value={c.candidatePotential}
                        onChange={handlePotentialChange}
                      />
                    </td>

                    {/* Actions */}
                    <td className="py-3.5 px-4">
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          onClick={() => setSelectedCandidate(c)}
                          className="p-2 hover:bg-indigo-50 rounded-lg transition-colors group" title="View Evidence">
                          <Eye className="w-4 h-4 text-gray-400 group-hover:text-indigo-600" />
                        </button>
                        {c.recommendations.length > 0 && (
                          <button
                            onClick={() => handleGenerateRecs(c)}
                            disabled={generatingFor === c._id}
                            className="p-2 hover:bg-violet-50 rounded-lg transition-colors group disabled:opacity-50" title="Refresh AI recommendations">
                            <Sparkles className="w-4 h-4 text-gray-400 group-hover:text-violet-600" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          );
        })()}
      </div>

      {/* Evidence panel */}
      {selectedCandidate && (
        <CandidateEvidencePanel
          candidate={selectedCandidate}
          onClose={() => setSelectedCandidate(null)}
        />
      )}
    </div>
  );
}
